import { CANNABINOIDS, TERPENES, CANNABINOID_LABELS, TERPENE_LABELS, cannabinoidOverrideKey, terpeneOverrideKey } from '../PackageLabResultsOverrides';
import { terpeneKeys, terpenesMeta } from '@wysiwyg/moss-runtime';
import { hasMatchingColoradoPotencyMapping, isColoradoPotencyMappingSource } from './coloradoPotencyMapping';
import { parseLabMeasurement, selectLabPercent, selectManualLabWeight, compoundLabWeight } from './labMeasurement';
import { manualOverrideState, manualCompoundField, manualCompoundValue, manualCompoundSource } from './manualLabOverrides';

type RecordValue = Record<string, unknown>;
const record = (value: unknown): RecordValue => value && typeof value === 'object' && !Array.isArray(value) ? value as RecordValue : {};
export const hasApprovedLabResults = (coa: unknown): boolean => record(record(coa).resultSetProvenance).policyVersion === 2;
export type ApprovedLabRow = {
  source?: 'reported';
  key: string; rowId?: string; label: string; qualified: boolean; weight?: number; percent?: number; uom?: string;
  weightText?: string; percentText?: string; outcomeText?: string; resultText: string;
  perServingText?: string; perPackageText?: string;
};
const cannabinoidKeys = new Map(CANNABINOIDS.map(key => [key.toLowerCase(), key]));
const terpeneAliases = new Map<string, string>();
for (const key of terpeneKeys) {
  const canonical = TERPENES.find(candidate => candidate === key || (terpenesMeta[key] && terpenesMeta[candidate] === terpenesMeta[key]));
  if (canonical) terpeneAliases.set(key.toLowerCase(), canonical);
}
for (const key of TERPENES) terpeneAliases.set(key.toLowerCase(), key);

function reportedBasis(unit: unknown): string {
  if (unit === '%') return 'percent';
  if (unit === 'mg/unit') return 'perUnit';
  if (unit === 'mg/srv' || unit === 'mg/serving') return 'perServing';
  return typeof unit === 'string' && unit ? unit : 'unavailable';
}

/** Display only. Never mutate the canonical COA used for saving or printing. */
export function approvedLabRows(coa: unknown, kind: 'cannabinoid' | 'terpene'): ApprovedLabRow[] {
  if (!hasApprovedLabResults(coa)) return [];
  const results = record(coa).analyteResults;
  if (!Array.isArray(results)) return []; // Policy 2 never falls back to derived legacy values.
  const rows: ApprovedLabRow[] = [];
  if (kind === 'cannabinoid' && String(record(coa).stateCode ?? '').trim().toUpperCase() === 'CO') {
    const labels: Record<string, string> = { ...CANNABINOID_LABELS, activeThc: 'Active THC', activeCbd: 'Active CBD', exothc: 'exo-THC' };
    const seenMeasurements = new Set<string>();
    for (const value of results) {
      const group = record(value);
      const measurements = Array.isArray(group.measurements) ? group.measurements : [group];
      for (const value of measurements) {
        const report = record(value);
        const key = typeof report.analyteKey === 'string' ? report.analyteKey : '';
        if (!labels[key] || !isColoradoPotencyMappingSource(report.mappingSource)) continue;
        const unit = typeof report.unit === 'string' ? report.unit : '';
        const expectedUnit = report.measurementBasis === 'percent' ? '%' : report.measurementBasis === 'perUnit' ? 'mg/unit'
          : report.measurementBasis === 'perServing' ? 'mg/serving' : '';
        const rowId = `${key}-${String(report.measurementBasis ?? 'unavailable')}`;
        if (seenMeasurements.has(rowId)) continue;
        seenMeasurements.add(rowId);
        let identity: unknown;
        try { identity = JSON.parse(String(record(report.provenance).analyteKey ?? '')); } catch { /* Untrusted identity has no displayable value. */ }
        const amount = parseLabMeasurement(report.value);
        const available = hasMatchingColoradoPotencyMapping(report, identity) && unit === expectedUnit && Boolean(expectedUnit) && amount !== undefined;
        const qualifier = report.qualifier;
        const prefix = qualifier == null || qualifier === '' || qualifier === 0 || qualifier === '0' || qualifier === 'None' ? '' : `${String(qualifier)} `;
        const resultText = available ? `${prefix}${amount}${unit === '%' ? '%' : ` ${unit}`}` : 'Unavailable';
        rows.push({ source: 'reported', key, rowId, label: `${labels[key]}${expectedUnit ? ` (${expectedUnit})` : ''}`, qualified: prefix !== '', resultText,
          ...(!available ? { outcomeText: resultText } : unit === '%' ? { percent: amount, percentText: resultText }
            : { weight: amount, uom: unit, weightText: resultText }) });
      }
    }
    return rows;
  }
  const seen = new Set<string>();
  const reportedUnits = new Map<string, string>();
  // Every market can have multiple independent raw measurements for one
  // analyte. The primary amount is a compatibility summary, not the full report.
  const measurements = results.flatMap(value => {
    const group = record(value);
    const values: unknown[] = Array.isArray(group.measurements) ? group.measurements : [group];
    return values.map(value => ({ value, siblings: values }));
  });
  for (const { value, siblings } of measurements) {
    const report = record(value);
    let identity: unknown;
    try { identity = JSON.parse(String(record(report.provenance).analyteKey ?? '')); } catch { continue; }
    if (!Array.isArray(identity) || identity[0] !== kind || typeof identity[1] !== 'string') continue;
    const key = kind === 'cannabinoid' ? cannabinoidKeys.get(identity[1].toLowerCase()) : terpeneAliases.get(identity[1].toLowerCase());
    const unit = typeof report.unit === 'string' ? report.unit : '';
    const basis = reportedBasis(unit);
    const sameBasis = siblings.filter(row => reportedBasis(record(row).unit) === basis);
    const reportedName = sameBasis.length > 1 && typeof report.testTypeName === 'string'
      ? report.testTypeName : undefined;
    const rowId = `${key ?? ''}-${basis}${reportedName ? `-${reportedName}` : ''}`;
    if (!key || seen.has(rowId)) continue; // Keep the existing supported analyte list.
    seen.add(rowId);
    reportedUnits.set(rowId, reportedName ? '' : unit);
    const amount = parseLabMeasurement(report.value);
    const qualifier = report.qualifier;
    const prefix = qualifier == null || qualifier === '' || qualifier === 0 || qualifier === '0' || qualifier === 'None' ? '' : `${String(qualifier)} `;
    const resultText = amount !== undefined
      ? `${prefix}${amount}${unit === '%' ? '%' : unit ? ` ${unit}` : ''}`
      : prefix.trim() || (report.passed === false ? 'Failed' : report.passed === true ? 'Passed' : '—');
    rows.push({ source: 'reported', key, rowId, qualified: prefix !== '', label: reportedName || (kind === 'cannabinoid' ? CANNABINOID_LABELS[key] : TERPENE_LABELS[key]) || key,
      ...(amount === undefined ? { outcomeText: resultText } : unit === '%' ? { percent: amount, percentText: resultText } : amount !== undefined ? { weight: amount, uom: unit, weightText: resultText } : { outcomeText: resultText }),
      resultText });
  }
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.key, (counts.get(row.key) ?? 0) + 1);
  return rows.map(row => {
    // Preserve historical single-row labels and IDs used by editor controls.
    if (counts.get(row.key) === 1) {
      const single = { ...row };
      delete single.rowId;
      return single;
    }
    const unit = reportedUnits.get(row.rowId ?? '');
    return { ...row, label: unit ? `${row.label} (${unit})` : row.label };
  });
}

/** Shared summary date only; never infer it from the first document in a merged report. */
export function approvedLabTestedDate(coa: unknown): string | undefined {
  if (!hasApprovedLabResults(coa)) return undefined;
  const value = record(coa);
  return [record(value.lab).at, value.dateTested, value.testedDate]
    .find((date): date is string => typeof date === 'string' && date.trim().length > 0);
}

/** Supplemental display rows only; the caller retains its own selected main basis. */
export function supplementalColoradoCannabinoidRows(coa: unknown, key: string, weight: RecordValue,
  selected: { value: number | undefined; uom: string }): ApprovedLabRow[] {
  if (!hasApprovedLabResults(coa) || String(record(coa).stateCode ?? '').trim().toUpperCase() !== 'CO') return [];
  const rows: ApprovedLabRow[] = [];
  const label = CANNABINOID_LABELS[key] || (key === 'thc' ? 'THC' : key);
  const numerator = selected.uom.split('/')[0];
  const denominator = selected.uom.split('/')[1]?.toLowerCase();
  const selectedBasis = denominator === 'unit' ? 'perUnit' : denominator === 'srv' || denominator === 'serving' ? 'perServing' : undefined;
  for (const [basis, amount, unit] of [
    ['perUnit', parseLabMeasurement(weight.perUnit), `${numerator}/unit`],
    ['perServing', parseLabMeasurement(weight.srv), `${numerator}/serving`],
  ] as const) {
    if (amount === undefined || (selected.value !== undefined && selectedBasis === basis)) continue;
    const resultText = `${amount} ${unit}`;
    rows.push({ key, rowId: `${key}-${basis}`, label: `${label} (${unit}) (override)`, qualified: false,
      weight: amount, uom: unit, weightText: resultText, resultText });
  }
  return rows;
}

/** Effective saved summary only. Raw reported-batch consumers keep approvedLabRows. */
export function effectiveLabRows(coa: unknown, kind: 'cannabinoid' | 'terpene', manualLabOverrides: unknown): ApprovedLabRow[] {
  const flags = record(manualLabOverrides);
  const value = record(coa);
  const keys = kind === 'cannabinoid' ? [...CANNABINOIDS, 'thc'] : TERPENES;
  const overrideKey = kind === 'cannabinoid' ? cannabinoidOverrideKey : terpeneOverrideKey;
  const normalize = (key: string) => manualCompoundField(overrideKey(key).enabled)?.family;
  const explicitKeys = keys.filter(key => typeof manualOverrideState(flags, overrideKey(key).enabled) === 'boolean');
  const explicit = new Set(explicitKeys.map(normalize));
  const rows = approvedLabRows(coa, kind).filter(row => !explicit.has(normalize(row.key)));
  const compounds = record(value[kind === 'cannabinoid' ? 'ingredients' : 'terpenes']);
  for (const key of explicitKeys) {
    if (manualOverrideState(flags, overrideKey(key).enabled) !== true) continue;
    const enabled = overrideKey(key).enabled;
    const source = kind === 'terpene' ? manualCompoundSource(compounds, value.ingredients, enabled) : compounds;
    const compound = manualCompoundValue(source, key, enabled);
    const weight = compoundLabWeight(compound);
    const percent = selectLabPercent(compound);
    const serving = parseLabMeasurement(weight.srv);
    const pkg = parseLabMeasurement(weight.pkg);
    const { value: weightValue, uom } = selectManualLabWeight(weight, flags, overrideKey(key).enabled);
    const numerator = uom.split('/')[0];
    const percentText = percent === undefined ? undefined : `${percent}%`;
    const weightText = weightValue === undefined ? undefined : `${weightValue} ${uom}`;
    const label = (kind === 'cannabinoid' ? CANNABINOID_LABELS[key] : TERPENE_LABELS[key]) || (key === 'thc' ? 'THC' : key);
    if (percentText !== undefined || weightText !== undefined || (kind === 'cannabinoid' && (serving !== undefined || pkg !== undefined))) {
      rows.push({ key, label: `${label} (override)`,
        qualified: false, percent, percentText, weight: weightValue, uom, weightText,
        perServingText: serving === undefined ? undefined : `${serving} ${numerator}/srv`,
        perPackageText: pkg === undefined ? undefined : `${pkg} ${numerator}/pkg`,
        resultText: percentText ?? weightText ?? '' });
    }
    if (kind === 'cannabinoid') rows.push(...supplementalColoradoCannabinoidRows(coa, key, weight, { value: weightValue, uom }));
  }
  return rows;
}

export function effectivePackageLabSummary(coa: unknown, marketCode: string | undefined, manualLabOverrides: unknown) {
  const summary = packageLabSummary(coa, marketCode);
  const flags = record(manualLabOverrides);
  const value = record(coa);
  const lab = record(value.lab);
  const manualFields: string[] = [];
  const text = (input: unknown) => typeof input === 'string' ? input.trim() : '';
  if (manualOverrideState(flags, 'lab_testedBy_override') === true) { summary.testedBy = text(lab.name); manualFields.push('testedBy'); }
  if (manualOverrideState(flags, 'lab_laboratoryLicense_override') === true) { summary.licenseNumber = text(lab.licenseNumber); manualFields.push('licenseNumber'); }
  if (manualOverrideState(flags, 'lab_testedDate_override') === true) { summary.testedDate = text(value.dateTested); summary.multiple = false; manualFields.push('testedDate'); }
  return { ...summary, manualFields };
}

export const MULTIPLE_LAB_TESTS_MESSAGE = 'Multiple — see Batch Test Results';

/** Summarize selected results, not historical documents or attachment counts. */
export function packageLabSummary(coa: unknown, marketCode?: string): { testedBy: string; testedDate: string; licenseNumber: string; multiple: boolean } {
  const value = record(coa);
  const approved = hasApprovedLabResults(coa);
  const text = (input: unknown): string => typeof input === 'string' ? input.trim() : '';
  const lab = record(value.lab);
  const firstTest = !approved && Array.isArray(value.labTests) ? record(value.labTests[0]) : {};
  const legacySummary = {
    multiple: false,
    testedBy: text(lab.name || firstTest.name),
    testedDate: (approved ? approvedLabTestedDate(coa) : text(lab.at || value.dateTested || firstTest.at)) || '',
    licenseNumber: text(lab.licenseNumber || firstTest.licenseNumber),
  };
  if (marketCode?.trim().toUpperCase() !== 'CO') return legacySummary;

  const analytes = record(value.resultSetProvenance).analytes;
  const sources = approved && Array.isArray(analytes) ? analytes.map(record) : [];
  // Resolve stable identifiers across the entire selected set first: a third
  // result can link a facility-only entry to a license-only entry.
  const facilityId = (source: RecordValue): number | undefined =>
    typeof source.testingFacilityId === 'number' && Number.isSafeInteger(source.testingFacilityId) && source.testingFacilityId > 0
      ? source.testingFacilityId : undefined;
  const groups = sources.map(source => [source]);
  const mergeMatching = (matches: (first: RecordValue[], second: RecordValue[]) => boolean) => {
    for (let first = 0; first < groups.length; first++) {
      for (let second = first + 1; second < groups.length;) {
        if (matches(groups[first], groups[second])) {
          groups[first].push(...groups.splice(second, 1)[0]);
          second = first + 1; // Newly linked identifiers can connect an earlier group.
        } else second++;
      }
    }
  };
  const fields = (group: RecordValue[], key: string): Set<string> => new Set(group.map(source => text(source[key])).filter(Boolean));
  const facilities = (group: RecordValue[]): Set<number> => new Set(group.map(facilityId).filter((id): id is number => id !== undefined));
  const overlaps = <T,>(first: Set<T>, second: Set<T>): boolean => [...first].some(value => second.has(value));
  mergeMatching((first, second) => overlaps(facilities(first), facilities(second)));
  mergeMatching((first, second) => {
    const firstIds = facilities(first);
    const secondIds = facilities(second);
    // A reused license must never collapse two known, distinct facilities.
    return !(firstIds.size && secondIds.size && !overlaps(firstIds, secondIds))
      && overlaps(fields(first, 'laboratoryLicense'), fields(second, 'laboratoryLicense'));
  });
  const differentLabs = groups.some((first, index) => groups.slice(index + 1).some(second => {
    for (const [firstValues, secondValues] of [
      [facilities(first), facilities(second)],
      [fields(first, 'laboratoryLicense'), fields(second, 'laboratoryLicense')],
      [fields(first, 'laboratoryName'), fields(second, 'laboratoryName')],
    ] as [Set<string | number>, Set<string | number>][]) {
      if (firstValues.size && secondValues.size) return !overlaps(firstValues, secondValues);
    }
    return false; // Missing metadata alone cannot prove another laboratory.
  }));
  const dates = new Set(sources.map(source => text(source.testedDate).split('T')[0]).filter(Boolean));
  const multiple = differentLabs || dates.size > 1;
  if (sources.length === 0) return legacySummary;
  const selectedText = (key: string): string => sources.map(source => text(source[key])).find(Boolean) || '';
  return {
    multiple,
    testedBy: multiple ? MULTIPLE_LAB_TESTS_MESSAGE
      : selectedText('laboratoryName'),
    testedDate: multiple ? MULTIPLE_LAB_TESTS_MESSAGE
      : dates.size === 1 ? [...dates][0] : '',
    licenseNumber: multiple ? '' : selectedText('laboratoryLicense'),
  };
}
